"use client"
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { blogs } from '@/lib/data';
import { ArrowRight, Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';

export default function BlogsPage() {
  const [selectedTag, setSelectedTag] = useState<string>("All");
  const [showAllBlogs, setShowAllBlogs] = useState<boolean>(false);
  const tags = ["All", "AI", "Automation", "Chatbots", "Voice Agents", "RAG Systems", "Workflow Automation", "Custom Consultation", "AI Security"]
  const filteredBlogs = selectedTag === "All" ? blogs : blogs.filter((blog) => blog.tags?.includes(selectedTag));
  const displayedBlogs = showAllBlogs ? filteredBlogs : filteredBlogs.slice(0, 6);
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="w-full bg-secondary/50 py-20 md:py-28 h-[20rem]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold font-headline text-primary mb-4">AI Insights & Resources</h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Stay ahead with expert blogs, real-world use cases, and the latest trends in AI solutions for modern businesses. </p>
        </div>
      </section>

      {/* blogs Grid */}
      <section className="w-full py-16 md:py-24">
        <div className="flex flex-wrap gap-2 px-12 mb-10">
          {tags.map((tag) => (
            <Badge
              key={tag}
              variant={selectedTag === tag ? "default" : "outline"}
              className="text-sm font-medium cursor-pointer"
              onClick={() => setSelectedTag(tag)}
              role="button"
              aria-label={`Filter blogs by ${tag} tag`}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedTag(tag);
                }
              }}
            >
              {tag}
            </Badge>
          ))}
        </div>
        <div className="container mx-auto px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {displayedBlogs.map((blog) => (
              <Card key={blog.slug} className="flex flex-col transition-transform transform hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/50">
                <div className="relative w-full h-48 overflow-hidden rounded-t-lg">
                  <Image
                    src={blog.coverImage}
                    alt={blog.title}
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                </div>
                <CardHeader className="flex-row items-start gap-4 space-y-0">
                  <CardTitle className="font-headline text-xl mt-0">{blog.title}</CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <p className="text-muted-foreground">{blog.description}</p>
                </CardContent>
                <div className="p-6 pt-0 grid grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Image src={blog.authorAvatar} alt={blog.authorName} width={20} height={20} className="rounded-full w-6 h-6 border border-r-2 border-primary" />
                      <p className="text-muted-foreground">{blog.authorName}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <p className="text-muted-foreground">{blog.publishedDate}</p></div>
                  </div>
                  <Button asChild variant="link" className="p-0 h-auto">
                    
                    <Link
                      href={`/blog/${blog.slug}`}
                      aria-label={`Read more about ${blog.title}`}
                    >
                      Read More <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>

                  </Button>
                </div>
                <div className="absolute right-0 top-0 bg-primary text-white rounded-md px-2 py-1">
                  {blog.tags}
                </div>
              </Card>
            ))}
          </div>
          {!showAllBlogs && filteredBlogs.length > 6 && (
            <div className="text-center mt-8">
              <Button
                onClick={() => setShowAllBlogs(true)}
                variant="outline"
                className="px-8 py-2"
              >
                View All Blogs ({filteredBlogs.length - 6} more)
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
